import { Auth0Provider, AppState } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AuthErrorBoundary } from "../components/auth/AuthErrorBoundary";
import { AUTH_CONFIG } from "../config/auth.config";

interface AuthProviderProps {
  children: ReactNode;
}

// Extend Window interface for runtime config
declare global {
  interface Window {
    APP_CONFIG?: {
      auth0?: {
        domain?: string;
        clientId?: string;
        callbackUrl?: string;
        audience?: string;
      };
      labs?: Array<{
        id: string;
        name: string;
        description: string;
        url?: string;
        icon: string;
        labels: string[];
        type?: "user" | "service";
        enabled?: boolean;
        require_claim?: string;
        access_denied?: "hidden" | "disabled";
        cardUrl?: string;
        cardScope?: string;
        open_in_new_tab?: boolean;
      }>;
      labsBaseUrl?: string;
      labsAccessClaimNamespace?: string;
      roleClaimNamespace?: string;
      logoutUrl?: string;
    };
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Hooks must be called unconditionally at the top
  const navigate = useNavigate();

  // If Auth0 is disabled, pass through children without wrapping
  if (!AUTH_CONFIG.enabled) {
    return <>{children}</>;
  }

  // Read runtime configuration from window.APP_CONFIG
  const config = window.APP_CONFIG?.auth0;
  const domain = config?.domain;
  const clientId = config?.clientId;
  const callbackUrl = config?.callbackUrl || window.location.origin;
  const audience = config?.audience;

  // Validate required configuration
  if (!domain || !clientId) {
    console.error(
      "Auth0 configuration missing. Required: VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID"
    );
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <div>
          <h2>Auth0 Configuration Error</h2>
          <p>Missing required environment variables: VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID</p>
          <p>Please check your .env file and restart the application.</p>
        </div>
      </div>
    );
  }

  // Handle redirect after authentication - navigate to the saved returnTo path
  const onRedirectCallback = (appState?: AppState) => {
    // Use React Router navigate to ensure proper routing
    const returnTo = appState?.returnTo || "/";
    navigate(returnTo, { replace: true });
  };

  return (
    <AuthErrorBoundary>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        authorizationParams={{
          redirect_uri: callbackUrl,
          ...(audience && { audience }),
        }}
        cacheLocation="localstorage"
        useRefreshTokens={true}
        onRedirectCallback={onRedirectCallback}
      >
        {children}
      </Auth0Provider>
    </AuthErrorBoundary>
  );
}
